import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useDarkMode } from "../hooks/useDarkMode";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct } from "./theme";
import { SkeletonCard, NoData, SourceChip, SectionTitle, SrTable, DeltaBadge,
  Spark, BreakBadge, breakNote } from "./components";
import PulseLayout from "./PulseLayout";
import ItalyMap from "./ItalyMap";
import ItalySpendPanel from "./ItalySpendPanel";
import ItalySeasonalityPanel from "./ItalySeasonalityPanel";
import ItalyPlatformsPanel from "./ItalyPlatformsPanel";
import ItalyCitiesPanel from "./ItalyCitiesPanel";
import ItalyForecastPanel from "./ItalyForecastPanel";
import ItalyVulnerabilityPanel from "./ItalyVulnerabilityPanel";
import { regionName } from "./regionNames";
import { pulseGet } from "./api";

const FIELDS = [
  { key: "nights", field: "nights", diverging: false, fmt: "compact" },
  { key: "arrivals", field: "arrivals", diverging: false, fmt: "compact" },
  { key: "yoy", field: "nights_yoy_pct", diverging: true, fmt: "pct" },
  { key: "foreign", field: "foreign_share_pct", diverging: false, fmt: "pct" },
  { key: "platform", field: "platform_intensity_pct", diverging: false, fmt: "pct" },
];

const Pill = ({ active, onClick, children, dm, testid }) => {
  const T = tokens(dm);
  return (
    <button onClick={onClick} aria-pressed={active} data-testid={testid}
      className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 whitespace-nowrap"
      style={{
        background: active ? T.brand : T.surface,
        color: active ? "#fff" : T.muted,
        border: `1px solid ${active ? T.brand : T.border}`,
      }}>
      {children}
    </button>
  );
};

const ProvincesTable = ({ region, dm }) => {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  useEffect(() => {
    let on = true;
    setData(null);
    pulseGet(`italy/regions/${region}/provinces`)
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData({ provinces: [] }); });
    return () => { on = false; };
  }, [region]);
  if (!data) return <SkeletonCard h={90} dm={dm} />;
  if (!data.provinces?.length) return <NoData dm={dm} h={70} />;
  return (
    <div data-testid={`italy-provinces-${region}`} className="px-2 py-2"
      style={{ background: T.surface2 }}>
      <p className="text-[10px] font-bold uppercase tracking-wider px-2 pb-1" style={{ color: T.muted }}>
        {t("it_provinces_title", { year: data.year })}
      </p>
      <table className="w-full text-xs">
        <thead>
          <tr style={{ color: T.muted }}>
            <th scope="col" className="text-left px-2 py-1 font-semibold">{t("it_col_province")}</th>
            <th scope="col" className="text-right px-2 py-1 font-semibold">{t("ind_nights")}</th>
            <th scope="col" className="text-right px-2 py-1 font-semibold">{t("yoy")}</th>
            <th scope="col" className="text-right px-2 py-1 font-semibold">{t("it_col_arrivals")}</th>
            <th scope="col" className="text-right px-2 py-1 font-semibold">{t("it_col_foreign")}</th>
          </tr>
        </thead>
        <tbody>
          {data.provinces.map((p) => (
            <tr key={p.code} style={{ borderTop: `1px solid ${T.border}` }}>
              <td className="px-2 py-1 font-medium">{p.label}</td>
              <td className="px-2 py-1 text-right pulse-num">{fmtCompact(p.nights, lang)}</td>
              <td className="px-2 py-1 text-right">
                <DeltaBadge pct={p.nights_yoy_pct} dm={dm} suffix=" " />
                {p.break_affected && <span className="ml-1" title={breakNote(data.series_break, lang)} style={{ color: dm ? "#f3cf6a" : "#8a5a00", fontSize: 10 }} aria-label="ISTAT 2025">⚠</span>}
              </td>
              <td className="px-2 py-1 text-right pulse-num">{fmtCompact(p.arrivals, lang)}</td>
              <td className="px-2 py-1 text-right pulse-num">{fmtPct(p.foreign_share_pct, lang, false)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function ItalyDeepDive() {
  const [dm] = useDarkMode();
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [source, setSource] = useState("ISTAT");
  const [fieldKey, setFieldKey] = useState("nights");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let on = true;
    setLoading(true);
    pulseGet("italy/regions", { source })
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [source]);

  const fieldCfg = FIELDS.find((f) => f.key === fieldKey);
  const fmtVal = (v) => (fieldCfg.fmt === "pct" ? fmtPct(v, lang, fieldCfg.diverging)
    : fmtCompact(v, lang));

  const mapValues = useMemo(() => {
    const out = {};
    (data?.regions || []).forEach((r) => { out[r.code] = r[fieldCfg.field] ?? null; });
    return out;
  }, [data, fieldCfg]);

  const regions = data?.regions || [];

  return (
    <PulseLayout active="italy">
      <div className="pulse-fade-up">
        <h1 className="pulse-serif text-4xl sm:text-5xl font-medium" data-testid="italy-title">
          {t("it_title")}
        </h1>
        <p className="text-sm mt-2 mb-8 max-w-3xl" style={{ color: T.muted }}>{t("it_sub")}</p>

        {/* ── P1: choropleth + league table + province drill-down ── */}
        <section className="mb-10" data-testid="italy-map-panel" aria-labelledby="italy-p1-title">
          <SectionTitle dm={dm} right={
            <div className="flex items-center gap-1 flex-wrap" role="group" aria-label={t("it_source_switch")}>
              <Pill dm={dm} active={source === "ISTAT"} onClick={() => setSource("ISTAT")}
                testid="italy-source-istat">ISTAT</Pill>
              <Pill dm={dm} active={source === "EUROSTAT"} onClick={() => setSource("EUROSTAT")}
                testid="italy-source-eurostat">Eurostat NUTS2</Pill>
            </div>
          }>
            <span id="italy-p1-title">{t("it_p1_title", { year: data?.year || "" })}</span>
          </SectionTitle>

          <div className="flex items-center gap-1 mb-4 flex-wrap" role="group" aria-label={t("it_ind_switch")}>
            {FIELDS.map((f) => (
              <Pill key={f.key} dm={dm} active={fieldKey === f.key}
                onClick={() => setFieldKey(f.key)} testid={`italy-field-${f.key}`}>
                {t(`it_ind_${f.key}`)}
              </Pill>
            ))}
          </div>

          {loading && <SkeletonCard h={420} dm={dm} />}
          {!loading && !regions.length && <NoData dm={dm} h={200} testid="italy-map-no-data" />}
          {!loading && regions.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              <div className="lg:col-span-2 p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <ItalyMap values={mapValues} format={fmtVal} diverging={fieldCfg.diverging}
                  dm={dm} lang={lang} selected={selected} hovered={hovered}
                  onSelect={(c) => setSelected(selected === c ? null : c)} onHover={setHovered}
                  noValueHint={source === "EUROSTAT" ? t("it_map_taa_note") : t("no_data")}
                  srCaption={`${t("it_p1_title", { year: data.year })} — ${t(`it_ind_${fieldKey}`)}`} />
                {source === "EUROSTAT" && (
                  <p className="text-[10px] mt-2" style={{ color: T.muted }} data-testid="italy-taa-note">
                    {t("it_map_taa_note")}
                  </p>
                )}
                <div className="mt-2">
                  <SourceChip source={source} dataset={data.dataset_code}
                    period={source === "ISTAT" ? data.as_of?.istat_monthly : data.year}
                    dm={dm} testid="italy-map-source" />
                </div>
              </div>

              <div className="lg:col-span-3 overflow-x-auto" style={{ border: `1px solid ${T.border}` }}
                data-testid="italy-league-table">
                {data.series_break && (
                  <div className="flex items-center gap-2 px-3 py-2 text-[11px]" style={{ background: T.surface2, color: T.muted }} data-testid="italy-league-break-note">
                    <BreakBadge brk={data.series_break} dm={dm} testid="italy-league-break-badge" />
                    <span className="truncate">{breakNote(data.series_break, lang)}</span>
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: T.muted, background: T.surface2 }}>
                      <th scope="col" className="text-left px-3 py-2 w-8">#</th>
                      <th scope="col" className="text-left px-2 py-2">{t("it_col_region")}</th>
                      <th scope="col" className="text-right px-2 py-2">{t("ind_nights")} {data.year}</th>
                      <th scope="col" className="text-right px-2 py-2">{t("yoy")}</th>
                      <th scope="col" className="text-right px-2 py-2">{t("rank_col_cagr")}</th>
                      <th scope="col" className="text-right px-3 py-2 w-24">{t("rank_col_trend")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {regions.map((r, i) => (
                      <React.Fragment key={r.code}>
                        <tr className="pulse-row-hover cursor-pointer"
                          data-testid={`italy-league-row-${r.code}`}
                          onClick={() => setSelected(selected === r.code ? null : r.code)}
                          onMouseEnter={() => setHovered(r.code)} onMouseLeave={() => setHovered(null)}
                          style={{
                            borderTop: `1px solid ${T.border}`,
                            background: (hovered === r.code || selected === r.code) ? T.surface2 : T.surface,
                          }}>
                          <td className="px-3 py-2 text-xs" style={{ color: T.muted }}>{i + 1}</td>
                          <td className="px-2 py-2 font-semibold">
                            <span className="inline-flex items-center gap-1.5">
                              {regionName(r.code, lang, r.label)}
                              {selected === r.code
                                ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
                                : <ChevronDown className="w-3.5 h-3.5" style={{ color: T.muted }} aria-hidden="true" />}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-right pulse-num">{fmtCompact(r.nights, lang)}</td>
                          <td className="px-2 py-2 text-right">
                            <DeltaBadge pct={r.nights_yoy_pct} dm={dm} suffix=" " />
                            {r.break_affected && <span className="ml-1" title={breakNote(data.series_break, lang)} aria-label={t("break_badge")} data-testid={`italy-league-break-${r.code}`} style={{ color: dm ? "#f3cf6a" : "#8a5a00", fontSize: 10 }}>⚠</span>}
                          </td>
                          <td className="px-2 py-2 text-right pulse-num">{fmtPct(r.cagr_5y_pct, lang)}</td>
                          <td className="px-3 py-1"><Spark data={r.spark} dm={dm} height={28} /></td>
                        </tr>
                        {selected === r.code && (
                          <tr>
                            <td colSpan={6} style={{ borderTop: `1px solid ${T.border}` }}>
                              <ProvincesTable region={r.code} dm={dm} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <SrTable caption={t("it_p1_title", { year: data.year })}
                  columns={[t("it_col_region"), t("ind_nights"), t("yoy"), t("rank_col_cagr"),
                            t("it_ind_foreign"), t("it_ind_platform")]}
                  rows={regions.map((r) => [regionName(r.code, lang, r.label),
                    fmtCompact(r.nights, lang), fmtPct(r.nights_yoy_pct, lang),
                    fmtPct(r.cagr_5y_pct, lang), fmtPct(r.foreign_share_pct, lang, false),
                    fmtPct(r.platform_intensity_pct, lang, false)])} />
              </div>
            </div>
          )}
        </section>

        <ItalyVulnerabilityPanel dm={dm} />
        <ItalyCitiesPanel dm={dm} />
        <ItalySpendPanel dm={dm} />
        <ItalyForecastPanel dm={dm} regions={regions.filter((r) => r.code !== "ITDA" || r.nights)} />
        <ItalySeasonalityPanel dm={dm} regions={regions.filter((r) => r.code !== "ITDA" || r.nights)} />
        <ItalyPlatformsPanel dm={dm} />
      </div>
    </PulseLayout>
  );
}
